import RightRail, { Route, RouterProvider } from '../../libs/gs-component-right-rail';
import Alert from '@xo-union/tk-component-alerts';
import { Formik, FormikHelpers } from 'formik';
import React, { useCallback, useContext, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import * as Yup from 'yup';
import DOMPurify from 'dompurify';

import {
  Maybe,
  usePageQuery,
  useUpdatePageMutation,
  useUpdateRegistryPageMutation,
  useWeddingWebsiteQuery,
  Wws_WeddingWebsiteAttributes,
} from '../../generated/graphql';
import {
  PAGE_NAMED_HOME_ERROR,
  PAGE_NAMED_RSVP_ERROR,
  PAGE_TAKEN_ERROR,
  PAGE_TITLE_BLANK_ERROR,
  STANDARD_ERROR_MESSAGE,
} from '../../helpers/errorMessage';
import { TrackingContext } from '../../TrackingContext';
import { ConfirmDelete } from './ConfirmDelete';
import { ConfirmDiscard } from './ConfirmDiscard';
import { EditPage, PAGE_DELETE, REGISTRY_NOTE } from './EditPage';
import { dashboardRoute, previewRoute } from '../../helpers/routes';

interface Props {
  preview?: boolean;
  className?: string;
}

export interface InitialValues {
  id?: Maybe<number>;
  title: string;
  show: boolean;
  type?: string;
  note?: string | null;
  weddingId?: Maybe<number>;
}

interface Variables {
  id: number;
  attributes: {
    title: string;
    show: boolean;
  };
}

export const validationSchema = Yup.object().shape({
  id: Yup.number().required(),
  title: Yup.string().required(PAGE_TITLE_BLANK_ERROR),
  show: Yup.boolean(),
  note: Yup.string(),
});

export function sanitizeHtml(html: string): string {
  let sanitizingOptions = {
    ALLOWED_TAGS: ['strong', 'b', 'i', 'em', 'p', 'br', 'a'],
    ALLOWED_ATTR: ['style', 'class', 'href', 'rel', 'target'],
  };
  return DOMPurify.sanitize(html, sanitizingOptions);
}

export const PageSettingsRail: React.FC<Props> = ({ preview = false, className }) => {
  const [updatePage, { loading: updatingPage }] = useUpdatePageMutation();
  const [updateRegistryPage, { loading: updatingRegistryPage }] = useUpdateRegistryPageMutation();
  const { data, loading } = useWeddingWebsiteQuery();
  const { track } = useContext(TrackingContext);

  const alertRef = useRef<any>(null);

  const showAndDismissAlert = useCallback((message: string) => {
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message,
      waitTime: 3000,
    });
  }, []);

  const history = useHistory();
  const match = useRouteMatch<{ pageId: string }>(
    `${preview ? previewRoute : dashboardRoute}/pages/:pageId/page-settings`
  );

  const { data: pageData } = usePageQuery({
    skip: !match?.params.pageId,
    variables: {
      id: Number(match?.params.pageId || ''),
    },
  });
  const modules: string[] = [];

  const onClose = () => {
    if (preview) {
      history.push(previewRoute);
    } else {
      history.push(dashboardRoute);
    }
  };

  const trackAction = (title: string) => {
    track('Item Action Initiated', {
      action: 'edit',
      actionName: `Edit Page`,
      page: title,
      item: null,
    });
  };

  const initialValues: InitialValues = {
    id: pageData?.page.id,
    title: pageData?.page.title || '',
    show: !!pageData?.page.show,
  };

  if (pageData?.page.type === 'RegistryPage' && !loading && data?.weddingWebsite) {
    const { registryNote, id } = data.weddingWebsite;
    initialValues.note = registryNote || '';
    initialValues.weddingId = id;
    modules.push(REGISTRY_NOTE);
  }

  if (pageData?.page.type === 'CustomPage') {
    modules.push(PAGE_DELETE);
  }

  const onSave = async (values: InitialValues, { setErrors }: FormikHelpers<InitialValues>): Promise<void> => {
    const variables: Variables = {
      id: values.id!,
      attributes: {
        title: values.title,
        show: values.show,
      },
    };

    if (modules.includes(REGISTRY_NOTE)) {
      //sanitize note
      if (values.note) values.note = sanitizeHtml(values.note);

      const weddingAttrs: Wws_WeddingWebsiteAttributes = { registryNote: values.note };
      await updateRegistryPage({ variables: { ...variables, weddingAttrs } });
      trackAction(values.title);
      onClose();
    } else {
      try {
        await updatePage({
          variables,
          update(_cache, { data }) {
            track('WWS Page Edited', {
              pageType: data?.updatePage?.type,
              action: 'edit',
              actionName: 'edit title',
              pageVisible: data?.updatePage?.show,
              pageTitle: data?.updatePage?.title,
            });
          },
        });
        trackAction(values.title);
        onClose();
      } catch (err) {
        if (err.message === 'GraphQL error: Title has already been taken') {
          setErrors({ title: PAGE_TAKEN_ERROR });
          showAndDismissAlert(PAGE_TAKEN_ERROR);
        } else if (err.message === 'GraphQL error: Custom page cannot be home') {
          setErrors({ title: PAGE_NAMED_HOME_ERROR });
          showAndDismissAlert(PAGE_NAMED_HOME_ERROR);
        } else if (err.message === 'GraphQL error: Custom page title cannot be rsvp') {
          setErrors({ title: PAGE_NAMED_RSVP_ERROR });
          showAndDismissAlert(PAGE_NAMED_RSVP_ERROR);
        } else {
          showAndDismissAlert(STANDARD_ERROR_MESSAGE);
        }
      }
    }
  };

  return (
    <RightRail open={!!match} className={className || ''} onEscapeKey={onClose} overlay={!preview}>
      <Formik enableReinitialize validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSave}>
        {({ submitForm, setFieldValue, dirty }): JSX.Element => (
          <RouterProvider entry="main">
            <Route
              screen="main"
              component={
                <EditPage
                  onClose={onClose}
                  submitForm={submitForm}
                  setFieldValue={setFieldValue}
                  isFormDirty={dirty}
                  isLoading={updatingPage || updatingRegistryPage}
                  modules={modules}
                />
              }
            />
            <Route screen="confirmDiscard" component={<ConfirmDiscard onClose={onClose} />} />
            <Route screen="confirmDelete" component={<ConfirmDelete pageId={pageData?.page.id} onClose={onClose} />} />
          </RouterProvider>
        )}
      </Formik>
      <Alert ref={alertRef} position="bottom" />
    </RightRail>
  );
};
